import React from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { useFormatMessage } from '../../../i18n-review/client/Msg';
import {
  NAV_PART,
  SEARCH_ICON_WRAP,
  SEARCH_INPUT,
  SEARCH_PLACEHOLDER_TEXT,
  SEARCH_SLOT_LABEL,
  SEARCH_TEASER_CARET,
  SEARCH_VALUE_LINE,
  SEARCH_WHAT_SLOT,
  SEARCH_WHAT_TEXT,
  searchWrapperClass,
  type SearchBarSizeVariant,
} from './nav-styles';
import { SearchFieldPeek, SearchGoButton, SearchNarrowMeter } from './SearchBarParts';
import { useSearchBarTeaser } from './useSearchBarTeaser';
import { searchBarMessages } from './search-bar-messages';
import { searchPanelMessages } from '../../search-panel/search-panel.messages';
import { preloadFacetIcons } from '../../../utils/search-panel/facet-icon-lazy';
import { preloadSearchPanel } from '../../search-panel/search-panel-lazy';

interface ClosedSearchBarProps {
  /** The whole rounded bar: SearchPanel's expand animation grows from this rect. */
  shellRef: React.RefObject<HTMLDivElement | null>;
  /** The What slot button: Escape hands focus back here once the panel has closed. */
  barRef?: React.RefObject<HTMLButtonElement | null>;
  /** Hidden (never unmounted) while the panel is open or animating closed. */
  hidden: boolean;
  placeholder: string;
  /** The current search text, shown in place of the placeholder when set. */
  query?: string;
  /** Explicit intent only: a click on the What slot or the count button, or Enter/Space/ArrowDown
   * or a printable character (passed as `seed`) while the What slot has focus. */
  onOpen: (seed?: string) => void;
  locationSegment?: React.ReactNode;
  filtersSegment?: React.ReactNode;
  /** Live count for the forest button (null: not known yet, so no number is shown). */
  count?: number | null;
  /** All open jobs: the narrowing line's denominator (null hides the line). */
  countTotal?: number | null;
  sizeVariant?: SearchBarSizeVariant;
}

/**
 * The closed state of the Sage search bar (2026-09-28), shared by Autocomplete.tsx (the SRP and
 * the LiveLauncher) and SearchLauncher.tsx's FallbackLauncher so both hydrated hosts render the
 * same bar. ResourceNavbar.tsx's prerendered twin builds the same shape from the same nav-styles
 * classes and SearchBarParts pieces, around a real <input> instead of this button.
 *
 * Focus alone never opens anything (the stricter rule every host follows): only `onOpen`'s
 * explicit-intent paths do. Pointer or focus anywhere on the bar stops the typewriter teaser.
 */
export const ClosedSearchBar: React.FC<ClosedSearchBarProps> = ({
  shellRef,
  barRef,
  hidden,
  placeholder,
  query = '',
  onOpen,
  locationSegment,
  filtersSegment,
  count = null,
  countTotal = null,
  sizeVariant = 'compact40',
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const locale = intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const isDesktopBar = sizeVariant === 'compact40';
  const teaser = useSearchBarTeaser(locale, isDesktopBar && !query && !hidden);
  const displayCount = teaser.active && teaser.count !== null ? teaser.count : count;
  const meterDenominator = teaser.active ? (teaser.total ?? countTotal) : countTotal;
  const meterRatio = displayCount !== null && meterDenominator ? displayCount / meterDenominator : null;
  const preloadPanelCode = () => {
    void preloadSearchPanel().catch(() => {});
    void preloadFacetIcons().catch(() => {});
  };
  const open = (seed?: string) => {
    teaser.stop();
    onOpen(seed);
  };

  return (
    <div
      ref={shellRef}
      data-jc-nav-part={NAV_PART.searchWrapper}
      className={searchWrapperClass(sizeVariant)}
      style={{ visibility: hidden ? 'hidden' : 'visible' }}
      // Intent to search: start fetching the panel's code (search-panel-lazy.ts) and its icon
      // tables (facet-icon-lazy.tsx), in case the idle-time fetch hasn't already.
      onPointerEnter={() => {
        teaser.stop();
        preloadPanelCode();
      }}
      onPointerDown={preloadPanelCode}
      onFocusCapture={() => {
        teaser.stop();
        preloadPanelCode();
      }}
    >
      <button
        ref={barRef}
        type="button"
        aria-label={placeholder}
        onClick={() => open()}
        onKeyDown={(e) => {
          // Explicit intent while the What slot itself has focus. Enter/Space/ArrowDown open with
          // today's query; a printable character opens AND seeds that character as the panel's
          // own first keystroke. Tab is deliberately not handled: it only moves focus.
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            open();
          } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            open(e.key);
          }
        }}
        className={SEARCH_WHAT_SLOT}
      >
        <span className={SEARCH_ICON_WRAP} aria-hidden>
          <MagnifyingGlass weight="bold" className="w-5 h-5" />
        </span>
        <span className={SEARCH_WHAT_TEXT}>
          <span className={SEARCH_SLOT_LABEL} aria-hidden>
            {formatMessage(searchBarMessages.whatLabel)}
          </span>
          <span className={SEARCH_VALUE_LINE} data-jc-nav-part={NAV_PART.searchInput}>
            {/* The typewriter text is decorative (aria-hidden); the button's own aria-label is the
             * placeholder, so screen readers never hear it type. */}
            {teaser.active ? (
              <span className={SEARCH_INPUT} aria-hidden>
                {teaser.text}
                <span className={SEARCH_TEASER_CARET} />
              </span>
            ) : query ? (
              <span className={SEARCH_INPUT}>{query}</span>
            ) : (
              <span className={`${SEARCH_INPUT} ${SEARCH_PLACEHOLDER_TEXT}`}>{placeholder}</span>
            )}
          </span>
        </span>
        {!query && isDesktopBar ? <SearchFieldPeek visible={!teaser.active || teaser.text.length === 0} /> : null}
      </button>
      {/* Chips sit inside the same container the panel opens from; clicking one focuses it, and
       * that focus must not bubble into anything that opens the panel (coordinator review round
       * 6, priority bug 2), so their focus events stop here. */}
      {locationSegment ? (
        <div className="contents" onFocus={(e) => e.stopPropagation()}>
          {locationSegment}
        </div>
      ) : null}
      {filtersSegment ? (
        <div className="contents" onFocus={(e) => e.stopPropagation()}>
          {filtersSegment}
        </div>
      ) : null}
      {isDesktopBar ? (
        <SearchGoButton
          countText={displayCount !== null ? intl.formatNumber(displayCount) : null}
          suffix={formatMessage(searchPanelMessages.jobsSuffix)}
          ariaLabel={
            displayCount !== null
              ? formatMessage(searchPanelMessages.showJobsButton, { count: intl.formatNumber(displayCount) })
              : placeholder
          }
          onClick={() => open()}
        />
      ) : null}
      {isDesktopBar ? <SearchNarrowMeter ratio={meterRatio} /> : null}
    </div>
  );
};
